import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import router from '@/router'

/**
 * Authentication Store
 * Single source of truth for user authentication state
 */
export const useAuthStore = defineStore('auth', () => {
  // State
  const token = ref<string>('')
  const userId = ref<number>(0)
  const studioId = ref<number>(0)

  // Computed
  const isAuthenticated = computed(() => !!token.value)

  // Actions
  const setToken = (newToken: string) => {
    token.value = newToken
  }

  const setUserId = (id: number) => {
    userId.value = id
  }

  const setStudioId = (id: number) => {
    studioId.value = id
  }

  /**
   * Handle jump token (SSO) authentication
   * Used when user comes with ?jump_token=xxx&studio_id=123
   */
  const handleJumpToken = (jumpToken: string, jumpStudioId?: string | number) => {
    setToken(jumpToken)
    if (jumpStudioId !== undefined) {
      setStudioId(Number(jumpStudioId))
    }
  }

  /**
   * Login with token
   */
  const login = (newToken: string, options?: { userId?: number; studioId?: number }) => {
    setToken(newToken)
    if (options?.userId !== undefined) {
      setUserId(options.userId)
    }
    if (options?.studioId !== undefined) {
      setStudioId(options.studioId)
    }
  }

  /**
   * 仅清除本地登录状态与各 store，不调用退出接口、不跳转。
   * 用于超管 jump_token 登录前先踢掉原账号等场景。
   */
  const clearAllWithoutRedirect = async () => {
    // 1. Stop all polling timers (must be done first to prevent ongoing requests)
    try {
      const { useTaskStore } = await import('@/store/task-store')
      const { usePicStore } = await import('@/store/pic-store')

      const taskStore = useTaskStore()
      const picStore = usePicStore()

      taskStore.stopPolling()
      picStore.stopPollRefreshEpisodePic()
      picStore.stopPollRepaintPic()
    } catch (error) {
      console.error('[AuthStore] Failed to stop polling:', error)
    }

    // 2. Clear all store states
    try {
      const { useTaskStore } = await import('@/store/task-store')
      const { useProjectStore } = await import('@/store/project-store')
      const { usePicStore } = await import('@/store/pic-store')
      const { useVideoStore } = await import('@/store/video-store')
      const { useAuditStore } = await import('@/store/audit-store')
      const { usePermissionStore } = await import('./permission-store')

      useTaskStore().resetTaskStore()
      useProjectStore().resetProjectStore()
      usePicStore().resetPicStore()
      useVideoStore().resetVideoStore()
      useAuditStore().resetAuditStore()
      usePermissionStore().clearPermissions()
    } catch (error) {
      console.error('[AuthStore] Failed to clear stores:', error)
    }

    // 3. Clear authentication state
    clearAuth()

    // 4. Clean up old localStorage keys (for legacy migration)
    try {
      localStorage.removeItem('k_sso_token')
      localStorage.removeItem('userInfo')
      localStorage.removeItem('userMenuConfig')
    } catch (error) {
      console.error('[AuthStore] Failed to clean up legacy localStorage:', error)
    }
  }

  /**
   * Logout and clear all auth state
   * Also stops all polling and clears all stores
   */
  const logout = async () => {
    await clearAllWithoutRedirect()

    // Redirect to login page
    if (location.pathname === '/') return
    await router.push('/login').catch(() => {
      window.location.href = window.location.origin + '/login'
    })
  }

  /**
   * Clear authentication state
   */
  const clearAuth = () => {
    token.value = ''
    userId.value = 0
    studioId.value = 0
  }

  // Migration: Load legacy data from old storage locations
  const migrateLegacyData = () => {
    // Only migrate if current store is empty
    if (token.value) return

    // Try to migrate from old storage locations
    const legacyToken = localStorage.getItem('k_sso_token')
    if (legacyToken) {
      token.value = legacyToken
      console.info('[AuthStore] Migrated token from legacy k_sso_token')
    } else {
      // Fallback to userInfo.token
      const userInfoStr = localStorage.getItem('userInfo')
      if (userInfoStr) {
        try {
          const userInfo = JSON.parse(userInfoStr) as { token?: string }
          if (userInfo.token) {
            token.value = userInfo.token
            console.info('[AuthStore] Migrated token from legacy userInfo')
          }
        } catch (error) {
          console.error('[AuthStore] Failed to parse legacy userInfo:', error)
        }
      }
    }

    // Clean up old storage locations after migration
    if (token.value) {
      localStorage.removeItem('k_sso_token')
      localStorage.removeItem('userInfo')
    }
  }

  return {
    // State
    token,
    userId,
    studioId,
    // Computed
    isAuthenticated,
    // Actions
    setToken,
    setUserId,
    setStudioId,
    handleJumpToken,
    login,
    logout,
    clearAuth,
    clearAllWithoutRedirect,
    migrateLegacyData,
  }
}, {
  persist: {
    key: 'auth',
    storage: localStorage,
  },
})
